import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { USERS_URL, REGISTER_URL, UPDATE_USER_URL } from '../apiConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faEye, faEyeSlash, faUserCheck } from '@fortawesome/free-solid-svg-icons';
import { TailSpin } from 'react-loader-spinner';
import { Modal, Button, Form, Alert, Pagination } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { ro } from 'date-fns/locale';
import debounce from 'lodash.debounce';
;


const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOption, setSortOption] = useState('name_asc');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [filterRole, setFilterRole] = useState('estudiante');
    const [filterStatus, setFilterStatus] = useState('active'); // Estado inicial como "Activos"
    const [loading, setLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [message, setMessage] = useState(null); 
    const [messageType, setMessageType] = useState(''); 
    const [formState, setFormState] = useState({
        last_name: '',
        first_name: '',
        username: '',
        email: '',
        password: '',
        role: 'Estudiante',
        allowed: true,
    });

    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Token ${token}` };

    const [filters, setFilters] = useState({
        search: '',
        allowed: 'true',
        // status: 'activo',
        start_date: '',
        end_date: '',
        ordering: '-date_joined',
        page: 1,
        page_size: 20,
        role: 'estudiante'
    });

    useEffect(() => {
        document.body.className = theme; // Aplica la clase 'light' o 'dark' al body
        }, []);

    const { data, isLoading, isError, isFetching, refetch } = useQuery({
        queryKey: ['users', filters],
        queryFn: async () => {
            const params = {
                search: filters.search,
                role: filters.role,
                allowed: filters.allowed,
                date_joined__gte: filters.start_date,
                date_joined__lte: filters.end_date,
                ordering: filters.ordering,
                page: filters.page,
                page_size: filters.page_size
            };
            
            const response = await axios.get(USERS_URL, { 
                headers,
                params 
            });
            // console.log(response.data)
            setUsers(response.data.results);
            // setFilteredUsers(response.data);
            return response.data;
        },
        keepPreviousData: true,
        staleTime: 30000
    });

    const handleSearch = React.useCallback(
        debounce((value) => {
            setFilters(prev => ({ ...prev, search: value, page: 1 }));
        }, 1000),
        []
    );

    useEffect(() => {
        return () => {
            handleSearch.cancel && handleSearch.cancel();
        };
    }, [handleSearch]);

    
    const handleSortChange = (e) => {
        setFilters(prev => ({...prev, 
            ordering: e.target.value ,
            page: 1
        }));
    };
    
    const handleRoleFilterChange = (e) => {
        setFilters(prev => ({...prev, 
            role: e.target.value, 
            page: 1
        }));
    };
    
    const handleStatusFilterChange = (e) => {
        setFilters(prev => ({...prev, 
            allowed: e.target.value, 
            page: 1
        }));
    };
    
    const handleDateChange = (e, isStart) => {
        const date = e.target.value;
        setFilters(prev => ({
            ...prev,
            page: 1,
            [isStart ? 'start_date' : 'end_date']: date
        }));
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setFormState({
            last_name: user.last_name,
            first_name: user.first_name,
            username: user.username,
            email: user.email,
            role: user.is_supervisor,
            allowed: user.allowed,
        });
        setShowForm(true);
    };
                                                                                                        
    const handleToggleUserStatus = async (user) => {
        const action = user.allowed ? 'inactivar' : 'reactivar'; // Detecta la acción según el estado actual
        if (window.confirm(`¿Estás seguro de que deseas ${action} a este usuario?`)) {
            setLoading(true);
            try {
                // Cambia el estado del usuario (si está activo, se desactiva; si está inactivo, se activa)
                await axios.put(
                    `${UPDATE_USER_URL.replace('<int:user_id>', user.id)}`,
                    { allowed: !user.allowed }, // Invierte el estado de `allowed`
                    { headers }
                );
                setMessage(`Usuario ${action} exitosamente`);
                setMessageType('success');
                refetch(); // Actualiza la lista de usuarios
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    console.error('Unauthorized, removing token');
                    localStorage.removeItem('token'); // Eliminar token si es un 401
                    alert('Debes iniciar sesión nuevamente.');
                    window.location.href = '/'; // Redirigir a la página de login
                } else {
                    console.error(`Error al ${action} el usuario:`, error);
                    setMessage(`Error al ${action} el usuario`);
                    setMessageType('danger');
                }
            } finally {
                setLoading(false);
            }
        }
    };
    
    const PaginationControls = () => {
        return data?.count > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <Pagination className="mb-0">
                <Pagination.Prev
                    onClick={() => setFilters(prev => ({...prev, page: prev.page - 1}))}
                    disabled={filters.page === 1 || isFetching}
                />
                
                {[...Array(Math.ceil(data.count / filters.page_size)).keys()].map(page => (
                    <Pagination.Item
                    key={page + 1}
                    active={page + 1 === filters.page}
                    onClick={() => setFilters(prev => ({...prev, page: page + 1}))}
                    >
                    {page + 1}
                    </Pagination.Item>
                ))}
                
                <Pagination.Next
                    onClick={() => setFilters(prev => ({...prev, page: prev.page + 1}))}
                    disabled={!data?.next || isFetching}
                />
                </Pagination>

                <div className="ms-3">
                <select 
                    className="form-select form-select-sm"
                    value={filters.page_size}
                    onChange={(e) => setFilters(prev => ({
                    ...prev,
                    page_size: Number(e.target.value),
                    page: 1
                    }))}
                >
                    {[10, 20, 50].map(size => (
                    <option key={size} value={size}>
                        {size} por página
                    </option>
                    ))}
                </select>
                </div>
            </div>
                );
            };
    
    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormState({
            ...formState,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    function CopyPassword({ password }) {
        const [copySuccess, setCopySuccess] = useState('');
    
        const copyToClipboard = () => {
            navigator.clipboard.writeText(password);
            setCopySuccess('¡Contraseña copiada!');
        };


    return (
        <div>
            <p>Usuario creado exitosamente.</p>     
            <p>Contraseña:</p>
            <div className="input-group">
                <input type="text" value={password} readOnly className="form-control" /> 
                <button className="btn btn-outline-secondary password-button" onClick={copyToClipboard}>Copiar</button>
            </div>
            {copySuccess && <small>{copySuccess}</small>}
        </div>
    );
}
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (selectedUser) {
                // console.log(formState)
                await axios.put(`${UPDATE_USER_URL.replace('<int:user_id>', selectedUser.id)}`, formState, { headers });
                setMessage('Usuario actualizado exitosamente');
            } else {
                const response = await axios.post(REGISTER_URL, formState, { headers });
                console.warn(response.data.password)
                setMessage(<CopyPassword password={response.data.password} />);
                        }
            setMessageType('success');
            refetch();
        } catch (error) {
            console.error('Error saving user:', error);
            setMessage('Error al guardar el usuario');
            setMessageType('danger');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNewUser = () => {
        setSelectedUser(null);
        setFormState({
            last_name: '',
            first_name: '',
            username: '',
            email: '',
            role: 'Estudiante',
            allowed: true,
        });
        setShowForm(true);
    };
    
    const toggleShowPassword = () => {
        setShowPassword(!showPassword); 
    };
    // Función para validar la contraseña
    const validatePassword = (password) => {
        let error = '';
        if (password.length < 8) {
        error = 'La contraseña debe tener al menos 8 caracteres.';
        } else if (!isNaN(password)) {
        error = 'La contraseña no puede ser completamente numérica.';
        }
        return error;
    };

    // Manejador del cambio de la contraseña
    const handlePasswordChange = (e) => {
        const { value } = e.target;

        // Validar la contraseña mientras el usuario escribe
        const error = validatePassword(value);
        setPasswordError(error);

        // Actualizar el estado del formulario
        handleFormChange(e);
    };
    
    return (
        <div className={`container mt-5 p-5 rounded ${theme === "light" ? "bg-light text-black" : "bg-dark text-white"}`}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1>Gestión de Usuarios</h1>
                {/* <button className="btn btn-outline-success" onClick={handleCreateNewUser}>
                    <FontAwesomeIcon icon={faPlus} /> Crear Usuario
                </button> */}
            </div>

            <div className="mb-3">
                <input
                    type="text"
                    className={`form-control mb-4 ${theme === "light" ? "" : "input-theme-black"}`}
                    placeholder="Buscar por nombre"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        handleSearch(e.target.value);
                      }}   
                    disabled={loading}
                    data-bs-theme={`${theme === "light" ? "light" : "dark"}`}
                />
                <div className="d-flex mb-4 justify-content-between">
                    <div className="d-flex">
                        <div className=' '>
                            <label>Fecha de inicio:</label>
                            <input  type="date" 
                                    className={`form-control mb-2 ${theme === "light" ? "" : "input-theme-black"}`}
                                    data-bs-theme={`${theme === "light" ? "light" : "dark"}`}
                                    value={filters.start_date}
                                    disabled={loading}
                                    onChange={(e) => handleDateChange(e, true)
                                  }/>
                        </div>
                        <div className=' ms-4'>
                            <label>Fecha de fin:</label>
                            <input type="date"  
                                    className={`form-control mb-2 ${theme === "light" ? "" : "input-theme-black"}`}
                                    data-bs-theme={`${theme === "light" ? "light" : "dark"}`}
                                    value={filters.end_date}
                                    disabled={loading}
                                    onChange={(e) => handleDateChange(e, false)
                                  }
                            />
                        </div>
                    </div>
                    <div className="d-flex">
                        <div className=' ms-4'>
                            <label>Ordenar por:</label>
                            <select 
                                value={filters.ordering}
                                onChange={handleSortChange} 
                                disabled={loading}
                                className={`form-select ${theme === "light" ? "" : "input-theme-black"}`}
                                data-bs-theme={`${theme === "light" ? "light" : "dark"}`}>
                                <option value="first_name">Nombres (A-Z)</option>
                                <option value="-first_name">Nombres (Z-A)</option>
                                <option value="-date_joined">Más nuevos primero</option>
                                <option value="date_joined">Más viejos primero</option>
                            </select>
                        </div>
                        <div className=' ms-4'>
                            <label>Filtrar por rol:</label>
                            <select
                                className={`form-select ${theme === "light" ? "" : "input-theme-black"}`}
                                data-bs-theme={`${theme === "light" ? "light" : "dark"}`}
                                value={filters.role}
                                onChange={handleRoleFilterChange} 
                                disabled={loading}>
                                    <option value="">Todos</option>
                                    <option value="supervisor">Supervisión</option>
                                    <option value="estudiante">Estudiantes</option>
                            </select>
                        </div>
                        <div className=' ms-4'>
                            <label>Filtrar por estado:</label>
                            <select 
                                className={`form-select  ${theme === "light" ? "" : "input-theme-black"}`}
                                data-bs-theme={`${theme === "light" ? "light" : "dark"}`}
                                value={filters.allowed} 
                                onChange={handleStatusFilterChange} 
                                disabled={loading}>
                                    {/* <option value="">Todos</option> */}
                                    <option value="true">Activos</option>
                                    <option value="false">Inactivos</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
            {loading || isFetching ?  (
                    <div className="d-flex justify-content-center mt-3">
                        <TailSpin color="#007bff" height={50} width={50} />
                    </div>
                        ) : (
                <div className="table-responsive m-5 p-3">
                    <table className={`table  ${theme === "light" ? "bg-light text-black" : "table-dark"}`}>
                    <thead className={`${theme === "light" ? "table-success" : "thead-dark"}`}>
                    <tr>
                                <th>Nombres</th>
                                <th>Correo</th>
                                <th>Rol</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.results?.map(user => (
                                <tr className='super-rows' key={user.id}>
                                    <td>{user.first_name} {user.last_name}</td>
                                    <td>{user.email}</td>
                                    <td>{user.is_supervisor == 'Supervisión' ? 'Supervisión' : 'Estudiante'}</td>
                                    <td>{user.allowed ? 'Activo' : 'Inactivo'}</td>
                                    <td>
                                    <div className='super-actions'>
                                        <button className="btn btn-outline-primary me-2s action-button" onClick={() => handleEditUser(user)}>
                                            <FontAwesomeIcon icon={faEdit} /> Editar
                                        </button>
                                        {user.allowed ?
                                        (<button className="btn btn-outline-danger action-button" onClick={() => handleToggleUserStatus(user)}>
                                                <FontAwesomeIcon icon={faTrash} /> Inactivar
                                        </button>) :
                                        (<button className="btn btn-outline-success action-button" onClick={() => handleToggleUserStatus(user)}>
                                                <FontAwesomeIcon icon={faUserCheck} /> Reactivar
                                        </button>)
                                        }
                                    </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {data?.count > 0 && <PaginationControls />}
                </div>
            )}

            {showForm && (
                    <Modal show={showForm} 
                    onHide={() => { setShowForm(false); setMessage(null); }}
                    className={` ${theme === 'dark' ? 'modal-dark' : 'modal-light'} custom-modal`} // Clase personalizada
                    >
                    <Modal.Header closeButton
                        className={`p-3 ${theme === 'dark' ? 'bg-dark text-white' : 'bg-light text-dark'} custom-modal-header`} // Añadir clase
                    >
                        <Modal.Title>{selectedUser ? 'Editar Usuario' : 'Crear Usuario'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body
                       className={`p-3 ${theme === 'dark' ? 'bg-dark text-white' : 'bg-light text-dark'}`}>
                        {message && (
                            <Alert variant={messageType} onClose={() => setMessage(null)} dismissible>
                                {message}
                            </Alert>
                        )}
                        <Form onSubmit={handleFormSubmit}>
                            <Form.Group className="mb-3" controlId="formFirstName">
                                <Form.Label>Nombre</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="first_name"
                                    value={formState.first_name}
                                    onChange={handleFormChange}
                                    required
                                    className={`form-control mb-2 ${theme === "light" ? "" : "input-theme-black"}`}
                                    data-bs-theme={`${theme === "light" ? "light" : "dark"}`}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="formLastName">
                                <Form.Label>Apellido</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="last_name"
                                    value={formState.last_name}
                                    onChange={handleFormChange}
                                    required
                                    className={`form-control mb-2 ${theme === "light" ? "" : "input-theme-black"}`}
                                    data-bs-theme={`${theme === "light" ? "light" : "dark"}`}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="formUsername">
                                <Form.Label>Nombre de Usuario</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="username"
                                    value={formState.username}
                                    onChange={handleFormChange}
                                    required
                                    className={`form-control mb-2 ${theme === "light" ? "" : "input-theme-black"}`}
                                    data-bs-theme={`${theme === "light" ? "light" : "dark"}`}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="formEmail">
                                <Form.Label>Correo</Form.Label>
                                <Form.Control
                                    type="email"
                                    name="email"
                                    value={formState.email}
                                    onChange={handleFormChange}
                                    required
                                    className={`form-control mb-2 ${theme === "light" ? "" : "input-theme-black"}`}
                                    data-bs-theme={`${theme === "light" ? "light" : "dark"}`}
                                />
                            </Form.Group>
                            {selectedUser ? 
                            <Form.Group className="mb-3" controlId="formPassword">
                                <Form.Label>Contraseña</Form.Label>
                                <div className="input-group">
                                    <Form.Control
                                        type={showPassword ? 'text' : 'password'}
                                        name="password"
                                        value={formState.password}
                                        onChange={handlePasswordChange} // Cambié esta línea para usar la validación
                                        isInvalid={!!passwordError} // Marca el campo como inválido si hay un error
                                        className={`form-control mb-2 ${theme === "light" ? "" : "input-theme-black"}`}
                                        data-bs-theme={`${theme === "light" ? "light" : "dark"}`}
                                    />
                                    <Button
                                        variant="outline-secondary" className='password-button'
                                        onClick={toggleShowPassword}
                                    >
                                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                    </Button>
                                    <Form.Control.Feedback type="invalid">
                                        {passwordError}
                                    </Form.Control.Feedback>
                                </div>
                            </Form.Group>
                            : '' }
                            <Form.Group className="mb-3" controlId="formRole">
                                <Form.Label>Rol</Form.Label>
                                <Form.Select
                                    name="role"
                                    value={formState.role}
                                    onChange={handleFormChange}
                                    required
                                    className={`form-control mb-2 ${theme === "light" ? "" : "input-theme-black"}`}
                                    data-bs-theme={`${theme === "light" ? "light" : "dark"}`}
                                >
                                    <option value="Estudiante">Estudiante</option>
                                    <option value="Supervisión">Supervisión</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="formAllowed">
                                <Form.Check
                                    type="checkbox"
                                    name="allowed"
                                    label="Permitir acceso"
                                    checked={formState.allowed}
                                    onChange={handleFormChange}
                                />
                            </Form.Group>
                            <Button variant="primary" type="submit" >
                                {selectedUser ? 'Guardar Cambios' : 'Crear Usuario'
                                }
                               
                                </Button>
                        </Form>
                    </Modal.Body>
                </Modal>
            )}
        </div>
    );
};

export default UserManagement;
